import type { ResolvedPayzheBtpPolicy } from './payzheBtpSettings';
import { PAYZHE_BTP_APK_DEFAULT_PATH, PAYZHE_BTP_APK_URL } from './config';

/** Kotlin native release is typically 8–15 MB; legacy RN builds were larger. HTML error pages are tiny. */
export const PAYZHE_BTP_MIN_APK_BYTES = 3 * 1024 * 1024;

export type ApkDownloadInfo = {
  url: string;
  sizeBytes?: number;
  sha256?: string;
  sha256Url: string;
  contentTypeOk: boolean;
};

function toAbsoluteUrl(pathOrUrl: string): string {
  const trimmed = pathOrUrl.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (typeof globalThis.location === 'undefined') {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${globalThis.location.origin}${path}`;
}

export function resolveApkDownloadUrl(policy: ResolvedPayzheBtpPolicy | null): string {
  const fromSettings = policy?.apkDownloadUrl?.trim();
  if (fromSettings) {
    return toAbsoluteUrl(fromSettings);
  }
  if (PAYZHE_BTP_APK_URL) {
    return toAbsoluteUrl(PAYZHE_BTP_APK_URL);
  }
  return toAbsoluteUrl(PAYZHE_BTP_APK_DEFAULT_PATH);
}

export function resolveApkSha256Url(apkUrl: string): string {
  return apkUrl.endsWith('.apk') ? `${apkUrl}.sha256` : `${apkUrl}.sha256`;
}

function isApkContentType(contentType: string): boolean {
  const ct = contentType.toLowerCase();
  return (
    ct.includes('vnd.android.package-archive') ||
    ct.includes('application/octet-stream') ||
    ct.includes('application/apk')
  );
}

/** Preflight APK URL before opening download (HEAD + optional SHA256 sidecar). */
export async function fetchApkDownloadInfo(url: string): Promise<ApkDownloadInfo> {
  const sha256Url = resolveApkSha256Url(url);
  let sizeBytes: number | undefined;
  let contentTypeOk = true;

  try {
    const head = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    if (head.ok) {
      const ct = head.headers.get('content-type') ?? '';
      contentTypeOk = !ct || isApkContentType(ct);
      const len = head.headers.get('content-length');
      if (len) {
        const parsed = Number.parseInt(len, 10);
        if (Number.isFinite(parsed)) sizeBytes = parsed;
      }
    }
  } catch {
    /* offline or CORS — still allow download */
  }

  let sha256: string | undefined;
  try {
    const res = await fetch(sha256Url, { cache: 'no-store' });
    if (res.ok) {
      const line = (await res.text()).trim().split(/\s+/)[0];
      if (line && /^[a-f0-9]{64}$/i.test(line)) {
        sha256 = line.toLowerCase();
      }
    }
  } catch {
    /* optional sidecar */
  }

  return { url, sizeBytes, sha256, sha256Url, contentTypeOk };
}

export function formatApkSize(bytes: number | undefined): string | undefined {
  if (bytes === undefined || !Number.isFinite(bytes)) return undefined;
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.round(bytes / 1024)} KB`;
}

export async function openPayzheBtpApkDownload(url: string): Promise<ApkDownloadInfo> {
  const info = await fetchApkDownloadInfo(url);

  if (info.sizeBytes !== undefined && info.sizeBytes < PAYZHE_BTP_MIN_APK_BYTES) {
    throw new Error(
      'APK download looks invalid or incomplete (file too small). Retry on Wi‑Fi or contact support.',
    );
  }
  if (!info.contentTypeOk) {
    throw new Error(
      'APK download URL did not return an Android package. The server may be serving an error page — retry later.',
    );
  }

  try {
    globalThis.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    try {
      globalThis.location.href = url;
    } catch {
      /* ignore */
    }
  }

  return info;
}
